import type { AIProjectClient } from "@azure/ai-projects";

/** One file the hosted coder produced (m8t:artifacts contract). */
export interface Artifact {
  name: string;
  path: string;          // POSIX-relative to the coder's files dir ($HOME/files)
  mime: string;
  size_bytes: number;
  session_id: string;
  description?: string;
  preview?: string;      // inline bytes for small text only; absent for binaries
}

// Canonical fence (agents/coding-agent/coder/artifacts.py): ```json m8t:artifacts\n<JSON>\n```
const FENCE_RE = /```json m8t:artifacts\s*\n([\s\S]*?)\n```/;

/**
 * Split a hosted reply into display text (marker removed) + the artifact list.
 * Absence of the marker ≡ []. Malformed JSON ≡ [] — never throws.
 */
export function parseArtifacts(raw: string): { text: string; artifacts: Artifact[] } {
  const text = raw;
  const m = FENCE_RE.exec(text);
  if (!m) return { text: text.trimEnd(), artifacts: [] };
  let artifacts: Artifact[] = [];
  try {
    const parsed: unknown = JSON.parse(m[1]);
    if (Array.isArray(parsed)) artifacts = parsed as Artifact[];
  } catch {
    artifacts = [];
  }
  const clean = text.slice(0, m.index).trimEnd();
  return { text: clean, artifacts };
}

export class ArtifactDownloadError extends Error {
  constructor(public relPath: string, cause: unknown) {
    super(`Failed to download artifact "${relPath}": ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = "ArtifactDownloadError";
  }
}

const FILES_PREFIX = "files/"; // M8T_CODER_FILES_DIR basename ($HOME/files; the coder's default)

/** Pull an artifact's bytes from the hosted session sandbox. The marker `path`
 *  is ALWAYS relative to the coder's files dir ($HOME/files); the session API is
 *  rooted at $HOME — so we ALWAYS prepend the files-dir basename. The model can
 *  nest output under a literal `files/` subdir, yielding a marker path like
 *  "files/chart.png" → real location $HOME/files/files/chart.png; a startsWith
 *  guard would WRONGLY skip the prefix there (live-confirmed gateway failure). */
export async function downloadArtifact(
  project: AIProjectClient,
  agentName: string,
  sessionId: string,
  relPath: string,
): Promise<Buffer> {
  const apiPath = FILES_PREFIX + relPath;
  try {
    const res = await (project as unknown as {
      beta: { agents: { downloadSessionFile: (n: string, s: string, p: string) =>
        Promise<{ readableStreamBody?: NodeJS.ReadableStream }> } };
    }).beta.agents.downloadSessionFile(agentName, sessionId, apiPath);
    const stream = res.readableStreamBody;
    if (!stream) throw new Error("no readableStreamBody (browser blobBody is server-unsupported)");
    const chunks: Buffer[] = [];
    for await (const c of stream) {
      if (Buffer.isBuffer(c)) chunks.push(c);
      else if (typeof c === "string") chunks.push(Buffer.from(c));
      else chunks.push(Buffer.from(c));
    }
    return Buffer.concat(chunks);
  } catch (e) {
    if (e instanceof ArtifactDownloadError) throw e;
    throw new ArtifactDownloadError(relPath, e);
  }
}
